module ClubList

open Elmish
open Fable.Core
open Fable.React
open Fable.React.Props
open OnlineDPR.Application.Shared
open OnlineDPR.Application.Client
open OnlineDPR.Application.Client.Components
open Fetch

[<Emit("ClubDataSource")>]
let clubDataSource : string = jsNative

[<Emit("ClubToggleOnfitSyncUrl")>]
let clubToggleOnfitSyncUrl : string = jsNative

[<Emit("ClubRequestNewPasswordUrl")>]
let clubRequestNewPasswordUrl : string = jsNative


type Msg =
| ParseClubDataList of Result<ClubData list, string>
| ParseUpdatedClubDataResult of Result<ClubData, string>
| ParseUpdatedClubUserResult of Result<ClubData*ClubUser, string>
| ToggleOnFitSync of ClubData
| ResetPassword of ClubData*ClubUser

let model : ClubData list =
    []

let fetchClubs() = Http.fetchAs<ClubData list> clubDataSource [ Credentials RequestCredentials.Include ]
let initClubList =
    Cmd.OfPromise.either
        fetchClubs
        ()
        (ParseClubDataList)
        ((fun e -> Error(string e)) >> ParseClubDataList)

let toggleOnFitSync clubData =
    Cmd.OfPromise.either
        (Http.postData clubToggleOnfitSyncUrl)
        clubData
        (ParseUpdatedClubDataResult)
        ((fun e -> Error(string e)) >> ParseUpdatedClubDataResult)

let toTuple clubData clubUser =
    (clubData, clubUser)

let resetPassword (clubData : ClubData) (clubUser : ClubUser) =
    Cmd.OfPromise.either
        (Http.postData clubRequestNewPasswordUrl)
        clubUser
        (Result.bind (fun u -> toTuple clubData u |> Ok) >> ParseUpdatedClubUserResult)
        ((fun e -> Error(string e)) >> ParseUpdatedClubUserResult)

let init() =
    (ClubListModel model), initClubList

let update msg model =
    match msg with
    | ParseClubDataList (Ok dataList) -> dataList, []
    | ParseUpdatedClubDataResult (Ok result) -> let newModel = ListHelper.replace (fun x -> x.ClubId = result.ClubId) (fun _ -> result) model
                                                match newModel with
                                                | Some newModel -> newModel, []
                                                | None -> model, []
    | ParseUpdatedClubUserResult (Ok data) ->   let clubData, clubUser = data
                                                let newClubUser = ListHelper.replace (fun x -> x.UserId = clubUser.UserId) (fun _ -> clubUser) clubData.User
                                                match newClubUser with
                                                | Some newClubUser -> let newClubData = { clubData with User = newClubUser }
                                                                      let newModel = ListHelper.replace (fun x -> x.ClubId = newClubData.ClubId) (fun _ -> newClubData) model
                                                                      match newModel with
                                                                      | Some newModel -> newModel, []
                                                                      | None -> model, []
                                                | None -> model, []
    | ToggleOnFitSync clubData -> let newData = {clubData with IsUpdating = true}
                                  let replaced = ListHelper.replace (fun x -> x.ClubId = clubData.ClubId) (fun _ -> newData) model
                                  match replaced with
                                  | Some newModel -> newModel, toggleOnFitSync clubData
                                  | None -> model, []
    | ResetPassword (clubData, clubUser) -> let newData = {clubData with IsUpdating = true}
                                            let replaced = ListHelper.replace (fun x -> x.ClubId = clubData.ClubId) (fun _ -> newData) model
                                            match replaced with
                                            | Some newModel -> newModel, resetPassword clubData clubUser
                                            | None -> model, []
    | _ -> model, []

let renderUserSettings data (user: ClubUser) dispatch : ReactElement list =
    let disabledClass = match data.IsUpdating with
                        | true -> "disabled"
                        | false -> ""
    [ td [] [ str user.Name]
      td [ ClassName "no-wrap" ] [ str (user.Email |> Option.defaultValue "")]
      (match user.HasSentPasswordRecovery with
      | true -> td [] [ a [ Href "#"
                            ClassName ("btn btn-success ") ]
                          [ str "Passwort zurückgesetzt" ] ]
      | false ->  td [] [ a [ Href "#"
                              OnClick ( fun _ -> dispatch (ResetPassword (data, user)) )
                              ClassName ("btn btn-primary " + disabledClass) ]
                            [ str "Passwort zurücksetzen" ] ] ) ]


let renderClubData (dataList : ClubData list) dispatch : ReactElement list =
    dataList
    |> List.map (fun data -> let syncSliderTitle = if data.IsUpdating then
                                                    "Die Änderung wird gerade gespeichert"
                                                   else if not data.SyncDataFromOnfitAllowed then
                                                    "Für diesen Club dürfen keine DPR Daten aus OnFit übertragen werden."
                                                   else if data.SyncDataFromOnfitAllowed then
                                                    "Für diesen Club dürfen DPR Daten aus OnFit übertragen werden."
                                                   else
                                                    ""
                             let userCount = data.User.Length
                             [ tbody [] [ tr [] [ td [ RowSpan userCount ] [ str data.ClubNumber ]
                                                  td [ RowSpan userCount ] [ str data.B2B]
                                                  td [ RowSpan userCount ] [ str data.FranchisePartnerName]
                                                  td [ RowSpan userCount ] [ slider data.IsOnfitUser true None None None ]
                                                  td [ RowSpan userCount ] [ slider data.SyncDataFromOnfitAllowed data.IsUpdating (Some (fun _ -> dispatch (ToggleOnFitSync data))) None (Some syncSliderTitle) ]
                                                  (renderUserSettings data data.User.Head dispatch)
                                                  |> ofList
                                                  td [ ClassName "subtle"
                                                       RowSpan userCount ] [ str (sprintf "%A" data.ClubId) ]
                                                  td [ ClassName "subtle"
                                                       RowSpan userCount ] [ str (sprintf "%A" data.FranchisePartnerId) ] ]
                                          (data.User.Tail
                                          |> List.map (fun user -> tr [] (renderUserSettings data user dispatch))
                                          |> ofList) ] ]
                             |> ofList )

let view model dispatch =
    table [ ClassName "table table-bordered flip-content custom-stripes"]
          [ thead [ ClassName "flip-header" ]
                  [ tr [ ] [ th [ ClassName "no-wrap" ] [ str "Clubnummer" ]
                             th [ ClassName "no-wrap" ] [ str "B2B" ]
                             th [ ClassName "no-wrap" ] [ str "Name" ]
                             th [ ClassName "no-wrap" ] [ str "Onfit-Daten"
                                                          div [ ClassName "subtitle"] [ str "In Onfit liegen Daten vor"] ]
                             th [ ClassName "no-wrap" ] [ str "Onfit-Sync"
                                                          div [ ClassName "subtitle"] [ str "Datenübertragung aus Onfit"
                                                                                        br []
                                                                                        str "per Opt-In gestattet."] ]
                             th [ ClassName "no-wrap" ] [ str "Login" ]
                             th [ ClassName "no-wrap" ] [ str "Email" ]
                             th [ ClassName "no-wrap" ] [ str "Optionen" ]
                             th [ ClassName "no-wrap subtle" ] [ str "ClubId" ]
                             th [ ClassName "no-wrap subtle" ] [ str "FranchisePartnerId" ]]]
            (renderClubData model dispatch
            |> ofList)
          ]